import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import "../maatwerk/purchase-contract/MaatwerkUpload.css";
import "./DemoUploadKoop.css";
import { Trans, useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DemoErrorModal from "./DemoErrorModal";
import DemoLoadingModal from "./DemoLoadingModal";

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Typography,
} from "@mui/material";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Known voucher shown in the order summary; used for the instant "applied"
// display. The backend validates the code again when the order is created.
const VOUCHER_CODE = "HUURDERSBOND26";

// Backend tier values for each plan shown in the UI.
const PLAN_TIERS = {
  standard: "standard",
  premium: "premium",
  premiumPlus: "premium_plus",
};

// Must stay above the server's long-poll hold time (25s).
const PROGRESS_CLIENT_TIMEOUT_MS = 35000;
const PROGRESS_RETRY_DELAY_MS = 2000;

// The Mollie webhook can lag a few seconds behind the redirect back to us.
const ORDER_POLL_INTERVAL_MS = 2000;
const ORDER_POLL_TIMEOUT_MS = 60000;
const EXECUTE_RETRY_ATTEMPTS = 5;
const EXECUTE_RETRY_DELAY_MS = 2000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Loose international format: optional +, 8-15 digits, spaces/dashes/parens.
function isValidPhone(value) {
  const digits = value.replace(/\D/g, "");
  return (
    /^\+?[0-9()\s-]+$/.test(value.trim()) &&
    digits.length >= 8 &&
    digits.length <= 15
  );
}

async function responseError(response) {
  let detail = "Request failed.";
  try {
    detail = (await response.json()).detail || detail;
  } catch (parseError) {}
  const err = new Error(detail);
  err.status = response.status;
  return err;
}

const DemoUploadKoop = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t, i18n } = useTranslation();
  const [runConfig, setRunConfig] = useState({});

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [createRequestLoading, setCreateRequestLoading] = useState(false);
  const [startRunLoading, setStartRunLoading] = useState(false);
  const [error, setError] = useState(null);
  // The tier is chosen on the intro page and arrives via ?plan=<key>; it can
  // still be changed here via the order summary.
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const planParam = new URLSearchParams(search).get("plan");
    return PLAN_TIERS[planParam] ? planParam : "standard";
  });
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  // Contact details for the expert review (premium tiers only).
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmailError, setContactEmailError] = useState(false);
  const [contactPhoneError, setContactPhoneError] = useState(false);
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [showVoucherField, setShowVoucherField] = useState(false);
  const [voucherInput, setVoucherInput] = useState("");
  const [voucherError, setVoucherError] = useState(false);
  const [requestId, setRequestId] = useState(null);
  // Payment overlay: "checking" | "failed" | "pending" | "fileGone".
  const [paymentState, setPaymentState] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  // Set when a paid request lost its buffered upload (410): the next submit
  // re-uploads to this request id and skips the payment step.
  const [resumeRequestId, setResumeRequestId] = useState(null);
  const [orderPollNonce, setOrderPollNonce] = useState(0);
  // Set right before the browser navigates to the Mollie checkout; never
  // cleared, so the pay button stays disabled until the page unloads.
  const [redirecting, setRedirecting] = useState(false);

  // Condition questions are hidden; both answers default to "weet ik nog niet".
  const financieringChecked = 2;
  const keuringChecked = 2;

  const expertChecked = selectedPlan !== "standard";
  const expertCheckValue = { standard: 0, premium: 1, premiumPlus: 2 }[
    selectedPlan
  ];

  const privacyHref =
    i18n.language === "nl"
      ? "https://voorwaardenchecker.nl/nl/privacyverklaring"
      : "https://checkyourterms.com/en/privacy-statement";

  function handleBack() {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/koopakte${search}`);
  }

  function applyVoucher() {
    if (voucherInput.trim().toUpperCase() === VOUCHER_CODE) {
      setVoucherApplied(true);
      setVoucherError(false);
    } else {
      setVoucherError(true);
    }
  }

  function buildRunConfig() {
    return {
      language: i18n.language,
      email: expertChecked ? contactEmail.trim() : "info@checkyourterms.com",
      phone: expertChecked ? contactPhone.trim() : "",
      financiering_voorbehoud: financieringChecked,
      bouwkundige_keuring: keuringChecked,
      financiering_deadline: "",
      expert_check: expertCheckValue,
      expert_review_notes: "",
    };
  }

  // Flow: create_request -> start_maatwerk (upload only, the run is gated
  // behind payment) -> POST /order -> Mollie checkout -> redirect back to
  // /upload?order_id=... -> poll the order -> execute_maatwerk -> progress.
  async function createRequest() {
    const params = new URLSearchParams(search);
    const formData = new FormData();
    formData.append(
      "run_config",
      JSON.stringify({
        ...buildRunConfig(),
        is_wa: params.get("wa") === "1" ? 1 : 0,
        tisref: params.get("tisref") ?? null,
      })
    );

    const response = await fetch(
      `${REACT_APP_BACKEND_URL}/run/create_request_maatwerk`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) throw await responseError(response);
    return await response.json();
  }

  // Uploads the contract; the backend buffers it until the order is paid and
  // execute_maatwerk is called. Can be called again to replace the upload.
  async function uploadContract(reqId) {
    const formData = new FormData();
    formData.append("contract_file", runConfig.contract_file);
    formData.append(
      "run_config",
      JSON.stringify({
        name: "MAATWERK",
        email: expertChecked ? contactEmail.trim() : "info@checkyourterms.com",
        phone: expertChecked ? contactPhone.trim() : "",
        license_plate: "",
        purchase_type: "PARTICULIER",
        language: i18n.language,
        agreed_gtc: runConfig.agreed,
        agreed_disclaimer: runConfig.agreed,
        financiering_voorbehoud: financieringChecked,
        bouwkundige_keuring: keuringChecked,
        financiering_deadline: "",
        expert_check: expertCheckValue,
        opmerkingen: "",
        tisref: new URLSearchParams(search).get("tisref") ?? null,
      })
    );

    const response = await fetch(
      `${REACT_APP_BACKEND_URL}/run/start_maatwerk/${reqId}`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) throw await responseError(response);
    await response.json();
  }

  async function createOrder(reqId, tierValue, voucherCode) {
    const body = { tier: tierValue, request_id: reqId };
    if (voucherCode) body.voucher_code = voucherCode;

    const response = await fetch(`${REACT_APP_BACKEND_URL}/order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await responseError(response);
      // 400 is the backend's "invalid voucher code" response.
      if (response.status === 400) err.isVoucherError = true;
      throw err;
    }

    return await response.json();
  }

  const pollProgress = useCallback(
    async (reqId) => {
      // Long-poll get_progress: the server holds each request open until the
      // content changes relative to the fingerprint we send (or ~25s passes),
      // so we re-request immediately instead of polling on an interval.
      let fingerprint = null;
      let finished = false;

      while (!finished) {
        let progressData;

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            PROGRESS_CLIENT_TIMEOUT_MS
          );

          const progressResponse = await fetch(
            `${REACT_APP_BACKEND_URL}/run/get_progress/${reqId}` +
              (fingerprint
                ? `?fingerprint=${encodeURIComponent(fingerprint)}`
                : ""),
            { signal: controller.signal }
          );

          clearTimeout(timeoutId);

          if (!progressResponse.ok) {
            await delay(PROGRESS_RETRY_DELAY_MS);
            continue;
          }

          progressData = await progressResponse.json();
        } catch (pollError) {
          // Dropped connection or client-side timeout: brief pause so a
          // flaky network doesn't hammer the server with reconnects.
          await delay(PROGRESS_RETRY_DELAY_MS);
          continue;
        }

        fingerprint = progressData.fingerprint || fingerprint;

        if (progressData.status === "failed") {
          throw new Error(progressData.detail || "Request failed.");
        }

        if ((progressData.progress || 0) >= 100) {
          finished = true;
        }
      }

      navigate("/koopakte/results/" + reqId);
    },
    [navigate]
  );

  // Payment gate: only succeeds once the backend has a paid order for this
  // request. 402 = webhook not processed yet (retry), 409 = already started
  // (safe to just watch progress), 410 = the buffered upload is gone and the
  // user must re-upload (the paid order stays valid for the same request id).
  const executeRun = useCallback(
    async (reqId) => {
      setRequestId(reqId);
      setPaymentState("checking");

      try {
        let started = false;

        for (let attempt = 0; attempt < EXECUTE_RETRY_ATTEMPTS; attempt++) {
          const response = await fetch(
            `${REACT_APP_BACKEND_URL}/run/execute_maatwerk/${reqId}`,
            { method: "POST" }
          );

          if (response.ok || response.status === 409) {
            started = true;
            break;
          }

          if (response.status === 402) {
            await delay(EXECUTE_RETRY_DELAY_MS);
            continue;
          }

          if (response.status === 410) {
            setResumeRequestId(reqId);
            setPaymentState("fileGone");
            return;
          }

          throw await responseError(response);
        }

        if (!started) {
          throw new Error(t("maatwerkUpload.payment.executeFailed"));
        }

        setPaymentState(null);
        setStartRunLoading(true);
        await pollProgress(reqId);
      } catch (err) {
        setPaymentState(null);
        setError(err.message || "Something went wrong.");
        setShowErrorModal(true);
      } finally {
        setStartRunLoading(false);
      }
    },
    [pollProgress, t]
  );

  async function handleSubmit() {
    // Premium tiers need a way to reach the customer about the review.
    if (expertChecked) {
      const emailOk = EMAIL_REGEX.test(contactEmail.trim());
      const phoneOk = !contactPhone.trim() || isValidPhone(contactPhone);
      setContactEmailError(!emailOk);
      setContactPhoneError(!phoneOk);
      if (!emailOk || !phoneOk) return;
    }

    setCreateRequestLoading(true);
    setError(null);

    try {
      // Re-upload for an already paid order whose buffered file was lost.
      if (resumeRequestId) {
        await uploadContract(resumeRequestId);
        setResumeRequestId(null);
        await executeRun(resumeRequestId);
        return;
      }

      const reqId = await createRequest();
      setRequestId(reqId);
      await uploadContract(reqId);

      const order = await createOrder(
        reqId,
        PLAN_TIERS[selectedPlan],
        voucherApplied ? voucherInput.trim().toUpperCase() : null
      );

      if (order.status === "paid") {
        // Fully covered by the voucher: no checkout needed.
        await executeRun(order.request_id || reqId);
      } else if (order.checkout_url) {
        setRedirecting(true);
        window.location.assign(order.checkout_url);
      } else {
        throw new Error("Request failed.");
      }
    } catch (err) {
      if (err.isVoucherError) {
        setVoucherApplied(false);
        setVoucherError(true);
        setShowVoucherField(true);
      } else {
        setError(err.message || "Something went wrong.");
        setShowErrorModal(true);
      }
    } finally {
      setCreateRequestLoading(false);
    }
  }

  async function retryPayment() {
    // Prefer the still-open checkout; otherwise create a fresh order for the
    // same request (no re-upload needed).
    if (paymentOrder?.checkout_url) {
      setRedirecting(true);
      window.location.assign(paymentOrder.checkout_url);
      return;
    }

    if (!paymentOrder?.request_id || !paymentOrder?.tier) {
      setPaymentState(null);
      return;
    }

    try {
      setPaymentState("checking");
      const order = await createOrder(
        paymentOrder.request_id,
        paymentOrder.tier,
        null
      );

      if (order.status === "paid") {
        await executeRun(order.request_id);
      } else if (order.checkout_url) {
        setRedirecting(true);
        window.location.assign(order.checkout_url);
      } else {
        throw new Error("Request failed.");
      }
    } catch (err) {
      setPaymentState(null);
      setError(err.message || "Something went wrong.");
      setShowErrorModal(true);
    }
  }

  // Mollie redirects back here with ?order_id=... for every outcome (paid,
  // canceled, failed, expired). All page state is lost with the redirect, so
  // everything needed to resume comes from the order itself.
  const orderIdFromUrl = new URLSearchParams(search).get("order_id");

  useEffect(() => {
    if (!orderIdFromUrl) return;

    let cancelled = false;

    const pollOrder = async () => {
      setPaymentState("checking");
      const deadline = Date.now() + ORDER_POLL_TIMEOUT_MS;

      while (!cancelled) {
        let order = null;

        try {
          const response = await fetch(
            `${REACT_APP_BACKEND_URL}/order/${orderIdFromUrl}`
          );
          if (response.ok) {
            order = await response.json();
          }
        } catch (orderError) {}

        if (cancelled) return;

        if (order) {
          setPaymentOrder(order);

          if (order.status === "paid") {
            executeRun(order.request_id);
            return;
          }

          if (["canceled", "failed", "expired"].includes(order.status)) {
            setPaymentState("failed");
            return;
          }
        }

        if (Date.now() >= deadline) {
          setPaymentState("pending");
          return;
        }

        await delay(ORDER_POLL_INTERVAL_MS);
      }
    };

    pollOrder();

    return () => {
      cancelled = true;
    };
  }, [orderIdFromUrl, orderPollNonce, executeRun]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length) {
      setRunConfig((prev) => ({ ...prev, contract_file: acceptedFiles[0] }));
    }
  }, []);

  const hasFile = Boolean(runConfig.contract_file);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    disabled: hasFile,
    maxSize: 25 * 1024 * 1024,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
  });

  function fileSizeLabel(size) {
    if (!size) return "";
    return size >= 1024 * 1024
      ? `${(size / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.round(size / 1024)} KB`;
  }

  // The voucher covers the Standard check (€ 29,95); paid tiers only charge
  // the review on top of it.
  const orderTotal = voucherApplied
    ? selectedPlan === "standard"
      ? t("maatwerkUpload.order.free")
      : t(`maatwerkUpload.order.${selectedPlan}AddonAmount`)
    : selectedPlan === "standard"
    ? t("maatwerkUpload.order.amount")
    : t(`maatwerkUpload.expertCheck.plans.${selectedPlan}.price`);

  return (
    <div className="muk-page">
      <DemoErrorModal
        visible={showErrorModal}
        setVisible={() => {
          setShowErrorModal(false);
        }}
        errorMsg={error}
        unsetRequestId={() => setRequestId(null)}
      />
      <DemoLoadingModal
        requestId={requestId}
        visible={startRunLoading}
        setVisible={setStartRunLoading}
      />

      {/* Payment status overlay (Mollie checkout return + payment gate) */}
      <Dialog
        open={Boolean(paymentState)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            padding: "36px 32px",
            width: "440px",
            maxWidth: "92vw",
          },
        }}
      >
        {paymentState === "checking" && (
          <div className="duk-pay-body">
            <CircularProgress
              size={36}
              thickness={5}
              sx={{ color: "#439BD9" }}
            />
            <h2 className="duk-pay-title">
              {t("maatwerkUpload.payment.checkingTitle")}
            </h2>
            <p className="duk-pay-text">
              {t("maatwerkUpload.payment.checkingText")}
            </p>
          </div>
        )}

        {paymentState === "failed" && (
          <div className="duk-pay-body">
            <h2 className="duk-pay-title">
              {t("maatwerkUpload.payment.failedTitle")}
            </h2>
            <p className="duk-pay-text">
              {t("maatwerkUpload.payment.failedText")}
            </p>
            <div className="duk-pay-actions">
              <button
                type="button"
                className="duk-pay-primary"
                onClick={retryPayment}
              >
                {t("maatwerkUpload.payment.retry")}
              </button>
              <button
                type="button"
                className="duk-pay-secondary"
                onClick={() => setPaymentState(null)}
              >
                {t("maatwerkUpload.payment.close")}
              </button>
            </div>
          </div>
        )}

        {paymentState === "pending" && (
          <div className="duk-pay-body">
            <h2 className="duk-pay-title">
              {t("maatwerkUpload.payment.pendingTitle")}
            </h2>
            <p className="duk-pay-text">
              {t("maatwerkUpload.payment.pendingText")}
            </p>
            <div className="duk-pay-actions">
              <button
                type="button"
                className="duk-pay-primary"
                onClick={() => setOrderPollNonce((nonce) => nonce + 1)}
              >
                {t("maatwerkUpload.payment.checkAgain")}
              </button>
              <button
                type="button"
                className="duk-pay-secondary"
                onClick={() => setPaymentState(null)}
              >
                {t("maatwerkUpload.payment.close")}
              </button>
            </div>
          </div>
        )}

        {paymentState === "fileGone" && (
          <div className="duk-pay-body">
            <h2 className="duk-pay-title">
              {t("maatwerkUpload.payment.fileGoneTitle")}
            </h2>
            <p className="duk-pay-text">
              {t("maatwerkUpload.payment.fileGoneText")}
            </p>
            <div className="duk-pay-actions">
              <button
                type="button"
                className="duk-pay-primary"
                onClick={() => setPaymentState(null)}
              >
                {t("maatwerkUpload.payment.backToUpload")}
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Top bar: back + stepper */}
      <div className="muk-topbar">
        <button type="button" className="muk-back" onClick={handleBack}>
          <ChevronLeftIcon sx={{ fontSize: "20px" }} />
          {t("terug")}
        </button>

        <div className="muk-stepper">
          <div className="muk-step done">
            <span className="muk-step-circle">
              <CheckIcon sx={{ fontSize: "16px" }} />
            </span>
            <span className="muk-step-label">
              {t("maatwerkUpload.steps.contractType")}
            </span>
          </div>
          <span className="muk-step-line" />
          <div className="muk-step active">
            <span className="muk-step-circle">2</span>
            <span className="muk-step-label">
              {t("maatwerkUpload.steps.details")}
            </span>
          </div>
          <span className="muk-step-line" />
          <div className="muk-step">
            <span className="muk-step-circle">3</span>
            <span className="muk-step-label">
              {t("maatwerkUpload.steps.results")}
            </span>
          </div>
        </div>
      </div>

      {/* Title */}
      <div className="muk-title-row">
        <h1 className="muk-title">{t("maatwerkUpload.pageTitle.purchase")}</h1>
        {voucherApplied && (
          <span className="muk-title-badge">
            <LocalOfferOutlinedIcon sx={{ fontSize: "15px" }} />
            {t("maatwerkUpload.expertCheck.voucherBadge")}
          </span>
        )}
        <span className="muk-chip">
          <HomeOutlinedIcon sx={{ fontSize: "16px" }} />
          {t("maatwerkUpload.chipPurchase")}
        </span>
      </div>
      <p className="muk-subtitle">{t("maatwerkUpload.pageSubtitleNew")}</p>

      <div className="muk-grid">
        {/* Left column */}
        <div className="muk-col muk-col-main">
          {/* Document + check summary */}
          <div className="muk-card">
            <div className="muk-card-header">
              <span className="muk-card-title">
                <UploadFileIcon sx={{ color: "#0F3A58", fontSize: "22px" }} />
                {t(
                  `maatwerkUpload.${
                    hasFile ? "whatYouAreChecking" : "yourDocument"
                  }`
                )}
              </span>
              {!hasFile && (
                <span className="muk-card-meta">
                  {t("maatwerkUpload.fileRequirements")}
                </span>
              )}
            </div>

            {!hasFile && (
              <div
                {...getRootProps({
                  className: `muk-dropzone ${
                    isDragActive ? "drag-active" : ""
                  }`,
                  onClick: open,
                })}
              >
                <input {...getInputProps()} />
                <span className="muk-drop-icon">
                  <CloudUploadIcon sx={{ fontSize: "26px" }} />
                </span>
                <span className="muk-drop-title">
                  {t("maatwerkUpload.dropFileHere")}
                </span>
                <span className="muk-drop-or">{t("maatwerkUpload.or")}</span>
                <button
                  type="button"
                  className="muk-browse-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  {t("maatwerkUpload.browseFiles")}
                </button>
              </div>
            )}

            {hasFile && (
              <div className="muk-doc-row">
                <span className="muk-doc-icon">
                  <DescriptionOutlinedIcon sx={{ fontSize: "24px" }} />
                </span>
                <div className="muk-file-text">
                  <span className="muk-file-name">
                    {runConfig.contract_file.name}
                  </span>
                  <span className="muk-file-sub">
                    {fileSizeLabel(runConfig.contract_file.size)} ·{" "}
                    {t("maatwerkUpload.uploaded")}
                  </span>
                </div>
                <div className="muk-doc-actions">
                  <CheckCircleIcon
                    sx={{ color: "#2E9E5B", fontSize: "24px" }}
                  />
                  <button
                    type="button"
                    className="muk-doc-replace"
                    onClick={() =>
                      setRunConfig({ ...runConfig, contract_file: null })
                    }
                  >
                    {t("maatwerkUpload.replace")}
                  </button>
                </div>
              </div>
            )}

            <div className="muk-summary-banner">
              <span className="muk-summary-icon">
                <HomeOutlinedIcon sx={{ fontSize: "22px" }} />
              </span>
              <div className="muk-summary-body">
                <span className="muk-summary-title">
                  {t("maatwerkUpload.summaryTitlePurchase")}
                </span>
                <span className="muk-summary-text">
                  {t("maatwerkUpload.summaryText")}
                </span>
              </div>
              <button
                type="button"
                className="muk-summary-change"
                onClick={handleBack}
              >
                {t("maatwerkUpload.change")}
              </button>
            </div>

            <div className="muk-included">
              <span className="muk-included-title">
                {t("maatwerkUpload.included.title")}
              </span>
              <div className="muk-included-grid">
                {["everyClause", "risks", "ask", "pdf"].map((key) => (
                  <span key={key} className="muk-included-item">
                    <CheckIcon
                      sx={{ fontSize: "17px", color: "#2E9E5B", mt: "2px" }}
                    />
                    {t(`maatwerkUpload.included.${key}`)}
                  </span>
                ))}
              </div>
            </div>

            <div className="muk-meta-row">
              <span className="muk-meta-item">
                <AccessTimeIcon sx={{ fontSize: "17px" }} />
                {t("maatwerkUpload.metaResults")}
              </span>
              <span className="muk-meta-sep" />
              <span className="muk-meta-item">
                <HowToRegOutlinedIcon sx={{ fontSize: "17px" }} />
                {t("maatwerkUpload.metaExpert")}
              </span>
            </div>

            <p className="muk-legal-text">
              {t("maatwerkUpload.aiDisclaimerText")} {t("testDisclaimerLong")}{" "}
              {t("testDisclaimerLong2")}
            </p>
          </div>

        </div>

        {/* Right column */}
        <div className="muk-col muk-col-side">
          {/* Order summary */}
          <div className="muk-card">
            <div className="muk-card-header">
              <span className="muk-card-title">
                <LocalOfferOutlinedIcon
                  sx={{ color: "#0F3A58", fontSize: "22px" }}
                />
                {t("maatwerkUpload.order.title")}
              </span>
              <button
                type="button"
                className="muk-order-change"
                onClick={() => setShowPlanPicker((prev) => !prev)}
              >
                {t("maatwerkUpload.change")}
              </button>
            </div>

            {showPlanPicker && (
              <div className="muk-order-plans">
                {Object.keys(PLAN_TIERS).map((plan) => (
                  <button
                    key={plan}
                    type="button"
                    className={`muk-order-plan ${
                      selectedPlan === plan ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedPlan(plan);
                      setShowPlanPicker(false);
                    }}
                  >
                    <span
                      className={`muk-radio-dot ${
                        selectedPlan === plan ? "checked" : ""
                      }`}
                    />
                    <span className="muk-order-plan-title">
                      {t(`maatwerkUpload.expertCheck.plans.${plan}.title`)}
                    </span>
                    <span className="muk-order-plan-price">
                      {plan === "standard" && voucherApplied ? (
                        <>
                          <s className="muk-price-strike">
                            {t(
                              "maatwerkUpload.expertCheck.plans.standard.priceRegular"
                            )}
                          </s>{" "}
                          {t("maatwerkUpload.expertCheck.plans.standard.price")}
                        </>
                      ) : plan === "standard" ? (
                        t(
                          "maatwerkUpload.expertCheck.plans.standard.priceRegular"
                        )
                      ) : (
                        t(`maatwerkUpload.expertCheck.plans.${plan}.price`)
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="muk-order-lines">
              {selectedPlan === "premiumPlus" ? (
                // Premium plus is presented as one all-in price, not as the
                // Standard check plus a review on top.
                <div className="muk-order-line">
                  <span className="muk-order-label">
                    {t("maatwerkUpload.order.premiumPlusFull")}
                  </span>
                  <span className="muk-order-amount">
                    {t("maatwerkUpload.expertCheck.plans.premiumPlus.price")}
                  </span>
                </div>
              ) : (
                <>
                  <div className="muk-order-line">
                    <span className="muk-order-label">
                      {t("maatwerkUpload.order.productPurchase")}
                    </span>
                    <span className="muk-order-amount">
                      {voucherApplied ? (
                        <s className="muk-price-strike">
                          {t("maatwerkUpload.order.amount")}
                        </s>
                      ) : (
                        t("maatwerkUpload.order.amount")
                      )}
                    </span>
                  </div>
                  {selectedPlan === "premium" && (
                    <div className="muk-order-line">
                      <span className="muk-order-label">
                        {t("maatwerkUpload.order.premiumAddon")}
                      </span>
                      <span className="muk-order-amount">
                        {t("maatwerkUpload.order.premiumAddonAmount")}
                      </span>
                    </div>
                  )}
                </>
              )}
              {voucherApplied && (
                <div className="muk-order-line">
                  <span className="muk-order-label">
                    <LocalOfferOutlinedIcon
                      sx={{ color: "#1E7A3E", fontSize: "17px" }}
                    />
                    {t("maatwerkUpload.order.voucherLine", {
                      code: VOUCHER_CODE,
                    })}
                  </span>
                  <span className="muk-order-amount discount">
                    {t("maatwerkUpload.order.discount")}
                  </span>
                </div>
              )}
            </div>

            <div className="muk-order-total">
              <span>{t("maatwerkUpload.order.toPayNow")}</span>
              <span className="muk-order-total-price">
                {voucherApplied && (
                  <s className="muk-price-strike">
                    {selectedPlan === "standard"
                      ? t("maatwerkUpload.order.amount")
                      : t(
                          `maatwerkUpload.expertCheck.plans.${selectedPlan}.price`
                        )}
                  </s>
                )}
                <span className="muk-order-free">{orderTotal}</span>
              </span>
            </div>
            {voucherApplied && selectedPlan === "standard" && (
              <p className="muk-order-note">
                {t("maatwerkUpload.order.nothingCharged")}
              </p>
            )}

            {voucherApplied ? (
              <div className="muk-voucher-applied-box">
                <CheckCircleIcon sx={{ color: "#2E9E5B", fontSize: "22px" }} />
                <div className="muk-voucher-applied-body">
                  <span className="muk-voucher-applied-title">
                    {t("maatwerkUpload.order.voucherAppliedTitle")}
                  </span>
                  <span className="muk-voucher-applied-text">
                    {t("maatwerkUpload.order.voucherAppliedText")}
                  </span>
                </div>
              </div>
            ) : (
              <div className="muk-voucher-add">
                {!showVoucherField ? (
                  <button
                    type="button"
                    className="muk-voucher-toggle"
                    onClick={() => setShowVoucherField(true)}
                  >
                    <LocalOfferOutlinedIcon sx={{ fontSize: "17px" }} />
                    {t("maatwerkUpload.order.haveVoucher")}
                  </button>
                ) : (
                  <>
                    <div className="muk-voucher-form">
                      <input
                        className="muk-voucher-input"
                        value={voucherInput}
                        placeholder={t(
                          "maatwerkUpload.order.voucherPlaceholder"
                        )}
                        onChange={(e) => {
                          setVoucherInput(e.target.value);
                          setVoucherError(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") applyVoucher();
                        }}
                      />
                      <button
                        type="button"
                        className="muk-voucher-apply"
                        onClick={applyVoucher}
                      >
                        {t("maatwerkUpload.order.applyVoucher")}
                      </button>
                    </div>
                    {voucherError && (
                      <p className="muk-voucher-error">
                        {t("maatwerkUpload.order.voucherInvalid")}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {expertChecked && (
              <>
                <div className="muk-order-sep" />

                <div className="muk-contact">
                  <span className="muk-contact-title">
                    {t("maatwerkUpload.expertContactTitle")}
                  </span>
                  <span className="muk-contact-text">
                    {t("maatwerkUpload.expertEmailDescription")}
                  </span>

                  <label className="muk-contact-field">
                    <span className="muk-contact-label">
                      {t("maatwerkUpload.expertEmailLabel")}
                    </span>
                    <input
                      type="email"
                      className={`muk-contact-input ${
                        contactEmailError ? "error" : ""
                      }`}
                      value={contactEmail}
                      placeholder={t("maatwerkUpload.expertEmailPlaceholder")}
                      onChange={(e) => {
                        setContactEmail(e.target.value);
                        setContactEmailError(false);
                      }}
                    />
                  </label>
                  {contactEmailError && (
                    <p className="muk-contact-error">
                      {t("maatwerkUpload.expertEmailInvalid")}
                    </p>
                  )}

                  <label className="muk-contact-field">
                    <span className="muk-contact-label">
                      {t("maatwerkUpload.expertPhoneLabel")}
                    </span>
                    <input
                      type="tel"
                      className={`muk-contact-input ${
                        contactPhoneError ? "error" : ""
                      }`}
                      value={contactPhone}
                      placeholder={t("maatwerkUpload.expertPhonePlaceholder")}
                      onChange={(e) => {
                        setContactPhone(e.target.value);
                        setContactPhoneError(false);
                      }}
                    />
                  </label>
                  {contactPhoneError && (
                    <p className="muk-contact-error">
                      {t("maatwerkUpload.expertPhoneInvalid")}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="muk-order-sep" />

            <div className="muk-order-info-box">
              <span className="muk-order-info-icon">
                <AutoAwesomeOutlinedIcon sx={{ fontSize: "20px" }} />
              </span>
              <div className="muk-order-info-body">
                <span className="muk-order-info-title">
                  {t("maatwerkUpload.aiDisclaimerTitle")}
                </span>
                <span className="muk-order-info-text">
                  {t("maatwerkUpload.aiDisclaimerWarningText")}
                </span>
              </div>
            </div>

            <div className="muk-consent">
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: "#439BD9",
                      "&.Mui-checked": { color: "#0F3A58" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ lineHeight: "1.7", fontSize: "14px" }}>
                    <Trans
                      i18nKey="avwAkkoord"
                      components={{
                        avw: (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={
                              i18n.language === "nl"
                                ? "https://voorwaardenchecker.nl/nl/algemene-voorwaarden"
                                : "https://checkyourterms.com/en/terms-and-conditions"
                            }
                          >
                            {i18n.language === "nl"
                              ? "Algemene Voorwaarden"
                              : "Terms and Conditions"}
                          </a>
                        ),
                        privacyverklaring: (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={privacyHref}
                          >
                            {i18n.language === "nl"
                              ? "Privacyverklaring"
                              : "Privacy statement"}
                          </a>
                        ),
                      }}
                    />
                  </Typography>
                }
                checked={runConfig?.agreed || false}
                onChange={(e) =>
                  setRunConfig({
                    ...runConfig,
                    agreed: e.target.checked,
                  })
                }
                sx={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "flex-start",
                  margin: 0,
                }}
              />
            </div>

            <Button
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardIcon />}
              sx={{
                marginTop: "18px",
                backgroundColor: "#E27C00",
                color: "#fff",
                borderRadius: "40px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                padding: "12px 28px",
                boxShadow: "0 4px 12px rgba(226, 124, 0, 0.35)",
                "&:hover": { backgroundColor: "#C96E00" },
                "&.Mui-disabled": {
                  backgroundColor: "#E3EBF2",
                  color: "#8BA3B5",
                },
              }}
              onClick={handleSubmit}
              disabled={
                !runConfig?.contract_file ||
                !runConfig?.agreed ||
                createRequestLoading ||
                startRunLoading ||
                redirecting
              }
            >
              {t(
                `maatwerkUpload.order.${
                  voucherApplied && selectedPlan === "standard"
                    ? "startWithAmount"
                    : "payWithAmount"
                }`,
                { amount: orderTotal }
              )}
            </Button>
            {voucherApplied && (
              <span className="muk-redeem-note">
                <LockOutlinedIcon sx={{ fontSize: "15px" }} />
                {t("maatwerkUpload.order.redeemNote")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoUploadKoop;
